import React, { Fragment, useCallback, useEffect, useRef } from 'react';
import type { FC, RefObject } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import type { AnnotationProviders } from '@atlaskit/editor-common/types';
import type { RendererActions } from '@atlaskit/renderer/actions';
import { AnnotationsWrapper } from '@atlaskit/renderer';
import { RendererActionsContext, WithRendererActions } from '@atlaskit/renderer/actions';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags';
import { withFlags } from '@confluence/flags/entry-points/withFlags';
import { useSubscriptionsDispatchContext } from '@confluence/graphql/entry-points/SubscriptionsContextProvider';
import { RendererAnnotationProviderComponents } from '@confluence/annotation-provider-inline-comments/entry-points/RendererAnnotationProviderComponents';
import { HoverComponent } from '@confluence/annotation-provider-inline-comments/entry-points/HoverComponent';
import { ViewComponent } from '@confluence/annotation-provider-inline-comments/entry-points/ViewComponent';
import { SelectionComponent } from '@confluence/annotation-provider-inline-comments/entry-points/SelectionComponent';
import { InlineCommentLinkHandler } from '@confluence/inline-comments-link-handler';
import { InlineCommentQueryParamsListener } from '@confluence/comments-query-param-handlers/entry-points/InlineCommentQueryParamsListener';
import {
	useAnnotations,
	useAnnotationsDispatch,
} from '@confluence/annotation-provider-store/entry-points/AnnotationsContext';
import {
	useDocumentUpdateStatus,
	useDocumentUpdateStatusDispatch,
} from '@confluence/annotation-provider-store/entry-points/DocumentUpdateStatusContext';
import { RendererActionsListener } from '@confluence/renderer-actions/entry-points/RendererActionsListener';
import { usePageState } from '@confluence/page-context';
import {
	MissedPubSubCommentEventsHandler,
	setUpRendererInlineCommentsPubSub,
	removeRendererInlineCommentsPubSub,
	CommentEventType,
	type MissedCommentsResult,
} from '@confluence/comments-pubsub';
import { ContentRefetchHandler } from '@confluence/content-body/entry-points/ContentRefetchHandler';
import {
	useContentRefetchDispatch,
	useContentRefetch,
} from '@confluence/content-body/entry-points/ContentRefetchContext';
import { useInlineCommentsActions } from '@confluence/inline-comments-hooks/entry-points/useInlineComments';
import { useUnreadCommentsActions } from '@confluence/unread-comments/entry-points/useUnreadComments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { PageSegmentLoadStart } from '@confluence/browser-metrics/entry-points/PageSegmentLoadStart';
import { FABRIC_COMMENTS_HIGHLIGHTS_METRIC } from '@confluence/annotation-provider-inline-comments/entry-points/pageSegmentLoadMetrics';
import { useCommentsDataActions } from '@confluence/comments-data/entry-points/useCommentsData';
import { useUpdateAnnotations } from '@confluence/comments-data/entry-points/useUpdateAnnotations';

type InlineCommentsPubSubHandler = {
	contentId: string;
	contentType?: 'page' | 'blogpost' | 'whiteboard' | 'database' | 'embed';
	updateDocument: (newADF: JSONDocNode) => void;
	rendererActions: RendererActions;
	isNestedRender?: boolean;
};

const InlineCommentsPubSubHandler: FC<InlineCommentsPubSubHandler> = ({
	contentId,
	contentType,
	updateDocument,
	rendererActions,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setDocumentUpdated } = useDocumentUpdateStatusDispatch();
	const { setRefetchContent } = useContentRefetchDispatch();
	const { documentNeedsUpdate, publishedDocumentVersion } = useDocumentUpdateStatus();
	const { addSubscriber, removeSubscriber, setContentType, setContentId } =
		useSubscriptionsDispatchContext() || {};
	const subscriptions = useRef<{ delta: string; subscriberId: string }[]>([]);
	const { setDocumentNeedsUpdate } = useDocumentUpdateStatusDispatch();
	const { userId } = useSessionData();
	const { updateUnresolvedInlineComment } = useInlineCommentsActions();
	const {
		setOrderedActiveAnnotationIdList,
		addReplyToCommentThread,
		addNewCommentThreads,
		updateRemovedCommentIdsMap,
		updateCommentBody,
	} = useCommentsDataActions();
	const { updateUnreadCommentsOnFirstRender, updateUnreadCommentIDSet } =
		useUnreadCommentsActions();
	const { storedCommentData } = useAnnotations();
	const { setStoredCommentData } = useAnnotationsDispatch();
	const latestStoredCommentRef = useRef(storedCommentData);

	const updateUnresolvedInlineCommentFn = useUpdateAnnotations([
		updateUnresolvedInlineComment,
		setOrderedActiveAnnotationIdList,
	]);

	useEffect(() => {
		latestStoredCommentRef.current = storedCommentData;
	}, [storedCommentData]);

	const subscribeToPubSub = useCallback(
		() =>
			(subscriptions.current =
				setUpRendererInlineCommentsPubSub({
					contentId,
					currentUserId: userId,
					contentType,
					rendererActions,
					updateDocument,
					updateUnresolvedInlineComment: fg('confluence-frontend-comments-panel')
						? updateUnresolvedInlineCommentFn
						: updateUnresolvedInlineComment,
					setContentNeedsUpdate: setDocumentNeedsUpdate,
					needsContentUpdate: Boolean(documentNeedsUpdate),
					publishedDocumentVersion,
					createAnalyticsEvent,
					addNewCommentThreads,
					addReplyToCommentThread,
					updateRemovedCommentIdsMap,
					updateCommentBody,
					storedCommentData: () => latestStoredCommentRef.current,
					setStoredCommentData,
					addSubscriber,
					setContentType,
					setContentId,
					updateUnreadCommentIDSet,
				}) || []),
		// We only need to respond to values that CAN change
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			userId,
			contentType,
			rendererActions,
			documentNeedsUpdate,
			publishedDocumentVersion,
			storedCommentData,
			setStoredCommentData,
			addSubscriber,
			setContentType,
			setContentId,
		],
	);

	const unsubscribeFromPubSub = useCallback(
		() =>
			removeRendererInlineCommentsPubSub({
				contentId,
				contentType,
				removeSubscriber,
				subscriptions: subscriptions.current,
			}),
		[contentId, contentType, removeSubscriber],
	);

	const handleFetchContentVersionEvent = useCallback(() => {
		setDocumentNeedsUpdate(true);
	}, [setDocumentNeedsUpdate]);

	const handleEventFetchSuccess = useCallback(
		(res: MissedCommentsResult) => {
			// TODO: Parse the events returned for 'resolved', 'unresolved', and 'delete' when they are added
			//       for now we just reload the content when the user comes back
			// For now all we have is create events, which means we will miss things, but only update if a new ones were created
			if (res.commentEvents.length > 0) {
				setDocumentUpdated(true);
				setRefetchContent(true);
				if (!fg('confluence_frontend_unread_comments_fixes')) {
					updateUnreadCommentsOnFirstRender(true);
				}
			}
		},
		[setRefetchContent, setDocumentUpdated, updateUnreadCommentsOnFirstRender],
	);

	if (!userId) {
		return null;
	} else {
		return (
			<MissedPubSubCommentEventsHandler
				contentId={contentId!}
				eventType={CommentEventType.INLINE}
				subscribeToPubSubEvents={subscribeToPubSub}
				unsubscribeFromPubSubEvents={unsubscribeFromPubSub}
				onEventFetchSuccess={handleEventFetchSuccess}
				onFetchContentVersionEvent={handleFetchContentVersionEvent}
				inactiveInterval={0}
				returnInterval={0}
			/>
		);
	}
};

type WithAnnotationsWrapperProps = {
	annotationProvider: AnnotationProviders | null | undefined;
	rendererRef: RefObject<HTMLDivElement>;
	adfDocument: DocNode;
	children: JSX.Element;
	isNestedRender?: boolean;
	updateDocument: (newADF: JSONDocNode) => void;
	lastModifiedDate?: string | null;
	isArchived?: boolean;
};

type BaseProps = WithAnnotationsWrapperProps & WithFlagsProps;

const WithAnnotationsWrapperComponent: FC<BaseProps> = ({
	flags,
	annotationProvider,
	rendererRef,
	adfDocument,
	updateDocument,
	isNestedRender = false,
	children,
	lastModifiedDate,
	isArchived = false,
}: BaseProps) => {
	const pageContainsBodiedMacrosRef = useRef(false);
	const { setDocumentUpdated, setDocumentNeedsUpdate, setPublishedDocumentVersion } =
		useDocumentUpdateStatusDispatch();
	const { refetchContent } = useContentRefetch();
	const { setRefetchContent } = useContentRefetchDispatch();
	const [{ contentId, contentType }] = usePageState();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { selectionProps, viewProps, hoverProps, isOffline } = useAnnotations();

	const { updateUnreadCommentsOnFirstRender } = useUnreadCommentsActions();

	const handleAnnotationsLoaded = useCallback(
		({ numberOfUnresolvedInlineComments }: { numberOfUnresolvedInlineComments: number }) => {
			const options = {
				customData: { isFabricPage: true, numberOfUnresolvedInlineComments },
			};
			FABRIC_COMMENTS_HIGHLIGHTS_METRIC.stop(options);
		},
		[],
	);

	const handleOnRefetchComplete = useCallback(
		(versionNumber?: number | null) => {
			setRefetchContent(false);
			setDocumentUpdated(true);
			setDocumentNeedsUpdate(false);
			if (!fg('confluence_frontend_unread_comments_fixes')) {
				updateUnreadCommentsOnFirstRender(true);
			}
			void flags.hideAllFlags();

			if (versionNumber) {
				setPublishedDocumentVersion(versionNumber);
			}

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'refetched',
					actionSubject: 'content',
					objectId: contentId,
					contentType,
					source: 'viewPageScreen',
				},
			}).fire();
		},
		[
			setRefetchContent,
			setDocumentUpdated,
			createAnalyticsEvent,
			contentId,
			contentType,
			setDocumentNeedsUpdate,
			flags,
			setPublishedDocumentVersion,
			updateUnreadCommentsOnFirstRender,
		],
	);

	// We need to check that there are page properties macros or excerpt macros on the page
	// so we can hide the delete option which causes weird issues in the document
	useEffect(() => {
		const bodiedMacroElem = document.querySelector('.ak-renderer-extension');
		pageContainsBodiedMacrosRef.current = Boolean(bodiedMacroElem);
	}, []);

	return annotationProvider ? (
		<RendererActionsContext>
			<WithRendererActions
				render={(actions) => {
					return (
						<Fragment>
							<PageSegmentLoadStart key={contentId} metric={FABRIC_COMMENTS_HIGHLIGHTS_METRIC} />
							<AnnotationsWrapper
								rendererRef={rendererRef}
								adfDocument={adfDocument}
								annotationProvider={annotationProvider}
								isNestedRender={isNestedRender}
								onLoadComplete={handleAnnotationsLoaded}
							>
								{!isNestedRender && <RendererAnnotationProviderComponents />}
								{selectionProps && !isNestedRender && (
									<SelectionComponent
										{...selectionProps}
										isOffline={isOffline}
										contentType={contentType}
										lastModifiedDate={lastModifiedDate}
									/>
								)}
								{hoverProps && !isNestedRender && (
									<HoverComponent {...hoverProps} isOffline={isOffline} />
								)}
								{viewProps && !isNestedRender && (
									<ViewComponent
										{...viewProps}
										isOffline={isOffline}
										pageContainsBodiedMacros={pageContainsBodiedMacrosRef.current}
									/>
								)}
								{children}
							</AnnotationsWrapper>
							{!isNestedRender && (
								<>
									<InlineCommentQueryParamsListener
										isArchived={isArchived}
										rendererRef={rendererRef}
									/>
									{fg('confluence_frontend_unread_comments_fixes') ? (
										<RendererActionsListener actions={actions} pageId={contentId} />
									) : (
										<RendererActionsListener actions={actions} />
									)}
								</>
							)}

							<InlineCommentsPubSubHandler
								contentId={contentId!}
								contentType={contentType}
								updateDocument={updateDocument}
								rendererActions={actions}
							/>
							<ContentRefetchHandler
								contentId={contentId}
								shouldRefetch={refetchContent}
								refetchImmediately
								onRefetchComplete={handleOnRefetchComplete}
							/>
							<InlineCommentLinkHandler />
						</Fragment>
					);
				}}
			/>
		</RendererActionsContext>
	) : (
		<RendererActionsContext>
			<>{children}</>
		</RendererActionsContext>
	);
};

export const WithAnnotationsWrapper = withFlags(WithAnnotationsWrapperComponent);
